import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AnalysisResult } from '../components/AnalysisResult';
import { CoachGradeCard } from '../components/CoachGradeCard';
import { ErrorLine } from '../components/ErrorLine';
import { FieldLines } from '../components/Field';
import { Leaders } from '../components/Leaders';
import { LiveIndicator } from '../components/LiveIndicator';
import { LoadingLine } from '../components/LoadingLine';
import { NarrativeCard } from '../components/NarrativeCard';
import { Score } from '../components/Score';
import { TabBar } from '../components/TabBar';
import { NotFoundPage } from './NotFoundPage';
import { WeatherStrip } from '../components/Weather';
import { api, isFinal, isLive, kickoff, type Game, type ReportCard, type TeamSide } from '../lib/api';
import { logo } from '../lib/logo';
import { useAnalysis } from '../lib/analysisCache';
import { MatchupMarks } from '../components/TeamMark';
import { duotone } from '../lib/colors';
import { useAllGames } from '../lib/useGames';

const TABS = [
  { id: 'predict', label: 'Predict' }, { id: 'betting', label: 'Betting' }, { id: 'grades', label: 'Grades' },
  { id: 'storylines', label: 'Storylines' }, { id: 'details', label: 'Details' },
];

function friendly(error: string): string {
  const e = error.toLowerCase();
  if (e.includes('budget')) return 'Daily analysis budget reached. Back tomorrow.';
  if (e.includes('limit')) return 'Analysis limit reached. Try again in a few minutes.';
  return 'Something went wrong.';
}

/** The call to action before an analysis runs: what you get, and one button. */
function Launch({ label, title, points, onRun, loading }: { label: string; title: string; points: string[]; onRun: () => void; loading: boolean }) {
  return (
    <div className="relative overflow-hidden bg-blue text-paper">
      <FieldLines />
      <div className="relative p-5 md:p-7">
        <h3 className="display text-[26px] leading-[0.95] md:text-[34px]">{title}</h3>
        <ol className="mt-5 grid grid-cols-1 md:grid-cols-3 md:gap-6">
          {points.map((p, i) => (
            <li key={p} className="flex items-start gap-3 border-t border-white/30 py-3">
              <span className="num pt-0.5 text-[12px] text-paper/70">{String(i + 1).padStart(2, '0')}</span>
              <span className="font-cond text-[16px] font-medium uppercase leading-snug tracking-[0.04em]">{p}</span>
            </li>
          ))}
        </ol>
        <div className="mt-5">
          {loading ? (
            <div>
              <p className="meta mb-2 !text-paper/85">Pulling live data, history and lines. About 20 seconds.</p>
              <div className="h-[3px] w-full overflow-hidden bg-white/30"><div className="h-full w-full origin-left bg-paper animate-fillLine" /></div>
            </div>
          ) : <button onClick={onRun} className="btn-on-blue">{label}</button>}
        </div>
      </div>
    </div>
  );
}

function PredictTab({ g, auto }: { g: Game; auto: boolean }) {
  const final = isFinal(g);
  const a = useAnalysis(g, final ? 'causation' : 'prediction', auto);
  if (a.error) return <ErrorLine message={friendly(a.error)} onRetry={a.run} />;
  if (final) {
    if (!a.data?.causation) return <Launch label="Break it down" title="What decided this game?" points={['The primary cause with evidence', 'The turning point and its swing', 'What was not the reason']} onRun={a.run} loading={a.loading} />;
    return <AnalysisResult r={a.data} game={g} />;
  }
  if (!a.data?.prediction) return <Launch label="Predict this game" title="Who wins, and by how much?" points={['A winner and expected margin', 'Win probability for both sides', 'The three factors that matter']} onRun={a.run} loading={a.loading} />;
  return <AnalysisResult r={a.data} game={g} />;
}

function BettingTab({ g, auto }: { g: Game; auto: boolean }) {
  const a = useAnalysis(g, 'betting', auto);
  if (a.error) return <ErrorLine message={friendly(a.error)} onRetry={a.run} />;
  if (!a.data?.betting_recommendation) return <Launch label="Find the edge" title="Is there value in this market?" points={['Kova probability vs every market', 'Edge, expected value, and stake size', 'The pattern for and against']} onRun={a.run} loading={a.loading} />;
  return <AnalysisResult r={a.data} game={g} />;
}

function GradesTab({ g }: { g: Game }) {
  const [card, setCard] = useState<ReportCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = isLive(g) || isFinal(g);
  const load = () => { setError(null); api.liveGrade(g.id, g.league ?? 'nfl').then(setCard).catch((e: Error) => setError(e.message)); };
  useEffect(() => { if (started) load(); }, [g.id, started]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLive(g)) return;
    const t = window.setInterval(load, 60_000);
    return () => window.clearInterval(t);
  }, [g.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!started) return <div className="card p-5"><p className="h2 !text-[22px]">Grades start at kickoff.</p><p className="body-muted mt-1">Every fourth down, kick, and timeout is graded as it happens against what usually works in that spot. Updates every minute.</p></div>;
  if (error) return <ErrorLine onRetry={load} />;
  if (!card) return <LoadingLine active />;
  const sides = [g.away_team, g.home_team];
  return (
    <div className="stack">
      {isLive(g) && <LiveIndicator label="Grading live" />}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {sides.map((side) => {
          const t = card.teams[side.abbreviation];
          if (!t) return null;
          const wp = t.overall.net_wp_impact_pct;
          return (
            <div key={side.abbreviation} className="card overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 text-paper" style={{ background: duotone(side.color, side.color) }}>
                {side.logo && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-white/90 p-1"><img src={logo(side.logo, 80)} alt="" className="h-full w-full object-contain" /></span>}
                <div className="min-w-0 flex-1">
                  <p className="display truncate text-[18px] leading-none">{side.name}</p>
                  {t.overall.head_coach && <Link to={`/coach/${encodeURIComponent(t.overall.head_coach)}?league=${g.league ?? 'nfl'}`} className="text-[13px] text-paper/85 hover:underline">{t.overall.head_coach}</Link>}
                </div>
                <div className="shrink-0 text-right"><p className="score text-[28px] leading-none">{wp > 0 ? '+' : ''}{wp.toFixed(1)}%</p><p className="meta !text-[10px] !text-paper/80">win prob. from calls</p></div>
              </div>
              <div className="grid grid-cols-4 gap-2 p-4 text-center">
                {([['Optimal', 'A', 'text-green'], ['Acceptable', 'B', 'text-blue'], ['Questionable', 'C', 'text-amber'], ['Wrong', 'F', 'text-red']] as const).map(([k, letter, color]) => (
                  <div key={k} className="bg-wash py-2"><p className={`score text-[24px] leading-none ${t.overall[k] ? color : 'text-faint'}`}>{t.overall[k]}</p><p className="meta mt-0.5 !text-[11px]">{letter}</p></div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {card.live_alerts && card.live_alerts.length > 0 && (
        <div>
          <p className="kicker mb-3">Calls worth a look</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{card.live_alerts.map((a, i) => <CoachGradeCard key={i} d={a} team={a.team} />)}</div>
        </div>
      )}
      <p className="text-[12px] text-muted">A is the best available call, F cost the most. Game management is priced in win probability; play calls in expected points against the best call.</p>
    </div>
  );
}

function StorylinesTab({ g, auto }: { g: Game; auto: boolean }) {
  const a = useAnalysis(g, 'narrative', auto);
  const s = a.data?.storylines ?? [];
  if (a.error) return <ErrorLine message={friendly(a.error)} onRetry={a.run} />;
  if (!s.length) return <Launch label="Find the storylines" title="What is the story of this matchup?" points={['Streaks and revenge spots', 'Primetime and rivalry splits', 'Each one with its number']} onRun={a.run} loading={a.loading} />;
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{s.map((x, i) => <NarrativeCard key={i} s={x} />)}</div>;
}

function DetailsTab({ g }: { g: Game }) {
  const k = kickoff(g.date);
  const started = isLive(g) || isFinal(g);
  const rows: [string, string | null | undefined][] = [
    ['Line', g.odds],
    ['Venue', g.venue],
    ['Broadcast', g.broadcast],
    ['Kickoff', `${k.day} ${k.time}`],
    [`${g.away_team.abbreviation} record`, g.away_team.record],
    [`${g.home_team.abbreviation} record`, g.home_team.record],
    ['Week', g.week ? `Week ${g.week}` : g.league === 'cfb' ? 'College' : 'NFL'],
    [started ? 'Total points' : 'City', started ? String(Number(g.away_team.score ?? 0) + Number(g.home_team.score ?? 0)) : (g.venue_city ?? null)],
    ['Setting', g.indoor === true ? 'Indoor' : 'Outdoor'],
    ['Away rank', g.away_team.rank ? `#${g.away_team.rank}` : null],
    ['Home rank', g.home_team.rank ? `#${g.home_team.rank}` : null],
  ];
  // exactly eight cells: a clean 2x4 whatever a given game happens to carry
  const shown = rows.filter(([, v]) => v).slice(0, 8);
  const padded: ([string, string | null | undefined] | null)[] = [...shown];
  while (padded.length < 8) padded.push(null);
  return (
    <div className="stack">
      {(isLive(g) || isFinal(g)) && <Leaders gameId={g.id} league={g.league ?? 'nfl'} />}
      <WeatherStrip gameId={g.id} league={g.league ?? 'nfl'} />
      <div>
        <p className="kicker mb-3">Game details</p>
        {/* one blue panel divided by hairlines, the way a stat strip is set under a score bug */}
        <div className="relative overflow-hidden rounded-md bg-blue text-paper">
          <FieldLines />
          <dl className="relative grid grid-cols-2 md:grid-cols-4">
            {padded.map((cell, i) => (
              <div key={i}
                className={`px-4 py-5 ${i % 2 === 1 ? 'border-l border-white/20' : ''} ${i >= 2 ? 'border-t border-white/20' : ''} ${i % 4 !== 0 ? 'md:border-l md:border-white/20' : 'md:border-l-0'} ${i >= 4 ? 'md:border-t' : 'md:border-t-0'}`}>
                {cell && (
                  <>
                    <dt className="meta !text-paper/75">{cell[0]}</dt>
                    <dd className="mt-1 font-display text-[17px] font-bold leading-tight">{cell[1]}</dd>
                  </>
                )}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

function HeaderTeam({ t, score, started, winner, align }: { t: TeamSide; score: string; started: boolean; winner: boolean; align: 'left' | 'right' }) {
  const right = align === 'right';
  return (
    <div className={`flex items-center gap-3 md:gap-4 ${right ? 'md:flex-row-reverse md:text-right' : ''}`}>
      {t.logo ? (
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-white/90 p-1.5 shadow-lift md:h-[72px] md:w-[72px]">
          <img src={logo(t.logo, 80)} alt="" className="h-full w-full object-contain" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="meta !text-paper/85">{t.rank ? `#${t.rank}  ` : ''}{t.abbreviation}{t.record ? `  ${t.record}` : ''}</p>
        <p className="display truncate text-[22px] leading-[0.95] md:text-[30px]">{t.name}</p>
      </div>
      {started && <Score value={score} className={`score text-[40px] leading-none md:text-[58px] ${winner ? 'text-paper' : 'text-paper/55'}`} />}
    </div>
  );
}

export function GamePage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { games, loading } = useAllGames();
  const g = useMemo(() => games.find((x) => x.id === id), [games, id]);
  // tell "still fetching" apart from "this game is not on the current scoreboard"
  if (!g && loading) return <section className="section"><div className="container"><LoadingLine active /></div></section>;
  if (!g) {
    return (
      <NotFoundPage title="That game is not on this week's board."
        text="Kova follows the current NFL and college schedules. Older games leave the scoreboard once the week rolls over." />
    );
  }
  const k = kickoff(g.date);
  const final = isFinal(g);
  const started = final || isLive(g);
  const a = Number(g.away_team.score ?? 0), h = Number(g.home_team.score ?? 0);
  const active = params.get('tab') ?? (final ? 'grades' : 'predict');
  const auto = params.get('run') === '1';
  const tabs = TABS.map((t) => (t.id === 'predict' && final ? { ...t, label: 'Breakdown' } : t));
  return (
    <>
      <section className="container pt-2 md:pt-4">
        <header className="relative overflow-hidden p-5 text-paper md:p-8" style={{ background: duotone(g.away_team.color, g.home_team.color) }}>
          <MatchupMarks away={g.away_team} home={g.home_team} />
          <div className="relative mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="pill bg-white/20 text-paper">{g.league === 'cfb' ? 'College' : 'NFL'}</span>
            {isLive(g) ? <span className="pill bg-red text-paper">{g.status_detail}</span>
              : <span className="meta !text-paper/90">{final ? 'Final' : `${k.day}  ${k.time}`}</span>}
            {g.venue && <span className="meta !text-paper/70">{g.venue}</span>}
            {g.odds && <span className="num text-[13px] text-paper/90">{g.odds}</span>}
          </div>
          <div className="relative grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-8">
            <HeaderTeam t={g.away_team} score={String(g.away_team.score ?? 0)} started={started} winner={!final || a >= h} align="left" />
            <span className="hidden font-cond text-[20px] font-semibold uppercase tracking-[0.1em] text-paper/60 md:block">at</span>
            <HeaderTeam t={g.home_team} score={String(g.home_team.score ?? 0)} started={started} winner={!final || h >= a} align="right" />
          </div>
        </header>
      </section>
      <section className="container pb-14 pt-5">
        <TabBar tabs={tabs} active={active} onChange={(t) => setParams({ tab: t })} />
        <div className="mt-6">
          {active === 'predict' && <PredictTab g={g} auto={auto} />}
          {active === 'betting' && <BettingTab g={g} auto={auto} />}
          {active === 'grades' && <GradesTab g={g} />}
          {active === 'storylines' && <StorylinesTab g={g} auto={auto} />}
          {active === 'details' && <DetailsTab g={g} />}
        </div>
      </section>
    </>
  );
}
